/**
 * Observability layer.
 * - Sentry for error tracking (grouping, source maps, release tracking)
 * - Web Vitals reporting (CLS, INP, LCP, FCP, TTFB) via log-ingest
 * - Structured event logging via log-ingest
 */
import * as Sentry from '@sentry/react';
import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';
import { supabase } from '@/integrations/supabase/client';

type LogLevel = 'info' | 'warn' | 'error';

interface LogEvent {
  level: LogLevel;
  category: 'vitals' | 'error' | 'event';
  name: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

const isProd = import.meta.env.PROD;

function report(event: LogEvent) {
  // Console output — readable in DevTools and replay tools
  const { level, category, name, data } = event;
  const tag = `[${category}:${name}]`;
  console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'](
    tag,
    data ?? ''
  );

  if (import.meta.env.PROD || (globalThis as any).__TEST_PROD__) {
    (async () => {
      try {
        let userId: string | undefined;
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          userId = sessionData?.session?.user?.id;
        } catch (_) {
          // Ignore session fetch errors
        }

        const body = {
          severity: event.level,
          message: (event.data?.message as string) || event.name,
          stack_trace: (event.data?.stack as string) || undefined,
          metadata: event.data || {},
          user_id: userId,
        };

        await supabase.functions.invoke('log-ingest', {
          body,
        });
      } catch (err) {
        // Swallow any logging exceptions, preventing telemetry loops
      }
    })();
  }
}

export function trackError(error: unknown, context?: Record<string, unknown>) {
  const err = error instanceof Error ? error : new Error(String(error));

  // Send to Sentry for error tracking (grouping, source maps, release tracking)
  Sentry.captureException(err, { extra: context });

  // Also send to log-ingest for web-vitals parity and historical consistency
  report({
    level: 'error',
    category: 'error',
    name: err.name,
    data: { message: err.message, stack: err.stack, ...context },
    timestamp: new Date().toISOString(),
  });
}

export function trackEvent(name: string, data?: Record<string, unknown>) {
  report({
    level: 'info',
    category: 'event',
    name,
    data,
    timestamp: new Date().toISOString(),
  });
}

function reportVital(metric: Metric) {
  report({
    level: metric.rating === 'poor' ? 'warn' : 'info',
    category: 'vitals',
    name: metric.name,
    data: {
      value: Math.round(metric.value * 100) / 100,
      rating: metric.rating,
      id: metric.id,
    },
    timestamp: new Date().toISOString(),
  });
}

let initialized = false;
export function initObservability() {
  if (initialized) return;
  initialized = true;

  // Web Vitals
  onCLS(reportVital);
  onINP(reportVital);
  onLCP(reportVital);
  onFCP(reportVital);
  onTTFB(reportVital);

  // Global error handlers
  window.addEventListener('error', (e) => {
    trackError(e.error || new Error(e.message), {
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    trackError(e.reason, { type: 'unhandledrejection' });
  });

  if (!isProd) {
    console.log('[observability] initialized');
  }
}
